<template>
  <section class="i-what-is-isnt">
    <div class="container">
      <div class="label mb-3">Set the record straight</div>
      <h2 class="headline">What this session is.<br />And what it isn't.</h2>
      <p
        class="body-lg"
        style="color: var(--text-body); margin-top: 12px; max-width: 560px"
      >
        We hear the same concern from nearly every client before they book:
        <em>"Is this just going to be a sales pitch for more coaching?"</em>
        Here's the direct answer.
      </p>

      <div class="is-isnt">
        <div class="is-isnt__col is-isnt__col--is">
          <div class="is-isnt__head green">What it is</div>
          <div
            v-for="item in isItems"
            :key="item"
            class="is-isnt__item"
          >
            <div class="is-isnt__icon is-isnt__icon--is">✓</div>
            <span v-html="item"></span>
          </div>
        </div>
        <div class="is-isnt__col is-isnt__col--isnt">
          <div class="is-isnt__head red">What it isn't</div>
          <div
            v-for="item in ISNTITEMS"
            :key="item"
            class="is-isnt__item"
          >
            <div class="is-isnt__icon is-isnt__icon--isnt">✕</div>
            <span v-html="item"></span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from "vue"

// PROPS
const props = defineProps({
  pricing: {
    type: String,
    default: "49"
  },
  timing: {
    type: String,
    default: "30"
  },
  highPricing: {
    type: String,
    default: "149.90"
  }
})

// COMPUTED
const isItems = computed(() => [
  `A ${props.timing}-minute 1:1 with a <strong>former recruiter or hiring manager</strong> — someone who decided who gets hired, not someone who passed a coaching exam`,
  "Specific to <strong>your report, your situation, and your next move.</strong> Not a template conversation.",
  "A <strong>written action plan delivered the same day.</strong> Specific title targets, positioning changes, and timeline.",
  `A <strong>one-time $${props.pricing} transaction</strong> (normally $${props.highPricing}). The session ends. The plan is yours. Nothing further is asked of you.`
])

// CONSTANTS
const ISNTITEMS = [
  "A sales call. The coach has no commission, and no quota.",
  'Generic advice. "Update your LinkedIn" and "network more" are not things you\'ll hear.',
  "A subscription, retainer, or package. No forced sell.",
  "Motivational. This isn't about mindset. It's about your specific next move — titles, companies, positioning, timing."
]
</script>

<style lang="scss" scoped>
.i-what-is-isnt {
  .is-isnt {
    display: grid;
    gap: 0;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    overflow: hidden;
    margin-top: 36px;
    grid-template-columns: 1fr;

    @include respond-to(680px) {
      grid-template-columns: 1fr 1fr;
    }

    &__col {
      padding: 32px;

      &--is {
        background: var(--green-lt);
        border-right: none;
        border-bottom: 1px solid var(--border);

        @include respond-to(680px) {
          border-right: 1px solid var(--border);
          border-bottom: unset;
        }
      }

      &--isnt {
        background: var(--red-lt);
      }
    }

    &__head {
      font-size: 0.75rem;
      font-weight: 800;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      margin-bottom: 20px;

      &.green {
        color: var(--green-dark);
      }

      &.red {
        color: var(--red);
      }
    }

    &__item {
      display: flex;
      gap: 10px;
      align-items: flex-start;
      margin-bottom: 14px;
      font-size: 0.875rem;
      line-height: 1.55;
    }

    &__icon {
      flex-shrink: 0;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 10px;
      font-weight: 800;
      margin-top: 1px;

      &--is {
        background: var(--green);
        color: #fff;
      }

      &--isnt {
        background: var(--red);
        color: #fff;
      }
    }
  }
}
</style>
